import "antd/dist/reset.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import CircularProgress from "@mui/material/CircularProgress";
import DropdownSelect from "../../FamilyTree/DropdownSelect";
import { renderNameWithColor } from "../../../Utils/colors";
import { getApi } from "../../../Services/apiService";
import { formatDashboardFamilyLineHeadName } from "../../../Utils/dashboardMemberDisplayName";
import {
  FilterTriggerLabel,
  filterTriggerClass,
  formatFilterValueList,
} from "../../../Utils/filterTriggerLabel";
import {
  compareByFamilyLine,
  compareByName,
  compareByState,
  formatPersonName,
  getHeadOfLineName,
  getMemberKey,
  getMemberState,
  getMemberStateAbbr,
  isAdultMember,
  isInLawMember,
  toPersonSummary,
} from "./familyNewsHelpers";
import { getStateAbbreviation, stateFilterOptions } from "../../../constants/states";
import { isLoggedInUserTreeAdminFromStorage } from "../../../Utils/subscriptionAccess";
import {
  DEFAULT_ARCHIVE_FOLDER,
  deleteFamilyNewsImage,
  postFamilyNews,
  putFamilyNews,
} from "../../../Services/familyNewsService";

const { RangePicker } = DatePicker;

/* The event types the Events modal opens a range picker for, so "Date of Event"
   behaves the same in both places. Kept in step with `shouldUseRangePicker` in
   CreateEventModal.jsx. */
const RANGE_EVENT_TYPES = new Set([
  "reunion",
  "family reunion",
  "family retreat",
  "other",
]);

const MAX_PHOTO_EDGE = 1400;
const PHOTO_QUALITY = 0.8;
const ACCEPTED_PHOTOS = "image/jpeg,image/jpg,image/png,image/svg+xml,image/webp";

const emptyForm = {
  host: "",
  date: "",
  titleId: "",
  title: "",
  endDate: "",
  theme: "",
  location: "",
  activities: "",
  words: "",
};

/**
 * Photos are held on the entry as data URLs until they are uploaded, so
 * downscale first. SVGs are read as-is since they cannot be drawn down usefully.
 */
const readPhotoFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.onload = () => {
      const dataUrl = reader.result;
      if (file.type === "image/svg+xml") {
        resolve({ name: file.name, dataUrl });
        return;
      }
      const img = new Image();
      img.onerror = () => resolve({ name: file.name, dataUrl });
      img.onload = () => {
        const scale = Math.min(
          1,
          MAX_PHOTO_EDGE / Math.max(img.width || 1, img.height || 1)
        );
        if (scale === 1) {
          resolve({ name: file.name, dataUrl });
          return;
        }
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({
          name: file.name,
          dataUrl: canvas.toDataURL("image/jpeg", PHOTO_QUALITY),
        });
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });

/** Photos already saved on the record come back from the API as URLs; the ones
    just picked in this modal are still data URLs waiting to be uploaded. */
const isNewPhoto = (photo) => String(photo?.dataUrl || "").startsWith("data:");

/** Photos live on the entry as data URLs; the API wants real files. */
const dataUrlToFile = (dataUrl, name) => {
  const match = /^data:([^;,]+)?[^,]*,(.*)$/s.exec(dataUrl || "");
  if (!match) return null;
  const mime = match[1] || "application/octet-stream";
  const isBase64 = /;base64/i.test(dataUrl.slice(0, dataUrl.indexOf(",")));
  const body = isBase64
    ? Uint8Array.from(atob(match[2]), (char) => char.charCodeAt(0))
    : new TextEncoder().encode(decodeURIComponent(match[2]));
  return new File([body], name || "photo", { type: mime });
};

function CreateFamilyNewsModal({
  isOpen,
  editMode = "Add",
  newsDetails,
  familyTreeId,
  onClose,
}) {
  const isView = editMode === "View";

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [eventTypes, setEventTypes] = useState([]);
  const [members, setMembers] = useState([]);
  // Invite table Email column is admin-only.
  const showEmailColumn = useMemo(() => isLoggedInUserTreeAdminFromStorage(), []);
  const [familyColorList, setFamilyColorList] = useState([]);

  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [guests, setGuests] = useState([]);
  const [guestInput, setGuestInput] = useState("");
  const [photos, setPhotos] = useState([]);
  // Photos the user took off an existing record: deleted on the server when
  // the edit is saved, not before, so cancelling leaves them alone.
  const [removedImageIds, setRemovedImageIds] = useState([]);

  // "Family in attendance" controls, mirroring the Events invite table
  const [searchMember, setSearchMember] = useState("");
  const [showFamilyColor, setShowFamilyColor] = useState(false);
  const [selectedFamilyColor, setSelectedFamilyColor] = useState([]);
  const [appliedFamilyColor, setAppliedFamilyColor] = useState([]);
  const [showStateFilter, setShowStateFilter] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const [selectedStates, setSelectedStates] = useState([]);
  const [appliedStates, setAppliedStates] = useState([]);
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");


  const colorRef = useRef(null);
  const stateRef = useRef(null);
  const multiPhotoInputRef = useRef(null);
  const singlePhotoInputRef = useRef(null);

  // Close the host / line-colour menus on an outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (colorRef.current && !colorRef.current.contains(e.target)) {
        setShowFamilyColor(false);
      }
      if (stateRef.current && !stateRef.current.contains(e.target)) {
        setShowStateFilter(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Same list the Events modal uses for "Event Type"
  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    getApi("/event-types")
      .then((response) => {
        if (!active) return;
        setEventTypes(Array.isArray(response?.data) ? response.data : []);
      })
      .catch(() => active && setEventTypes([]));
    return () => {
      active = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !familyTreeId) return;
    let active = true;
    getApi(
      `/family-members-for-invitation-with-spouses?family_tree_id=${familyTreeId}`
    )
      .then((response) => {
        if (!active) return;
        setMembers(response?.data?.members || []);
      })
      .catch(() => active && setMembers([]));
    getApi(
      `/family-members-count-by-filter?family_tree_id=${familyTreeId}&type=0`
    )
      .then((response) => {
        if (!active) return;
        setFamilyColorList(response?.data?.members || []);
      })
      .catch(() => active && setFamilyColorList([]));
    return () => {
      active = false;
    };
  }, [isOpen, familyTreeId]);

  // Load the entry being edited / viewed, or reset for a new one
  useEffect(() => {
    if (!isOpen) return;
    const detail = newsDetails || {};
    if (editMode !== "Add" && detail.id) {
      setForm({
        host: detail.hosts?.[0]?.id || "",
        date: detail.date || "",
        titleId: detail.title_id || "",
        title: detail.title || "",
        endDate: detail.end_date || "",
        theme: detail.theme || "",
        location: detail.location || "",
        activities: detail.activities || "",
        words: detail.words || "",
      });
      setSelectedAttendees(detail.attendees || []);
      setGuests(detail.guests || []);
      setPhotos(detail.photos || []);
    } else {
      setForm(emptyForm);
      setSelectedAttendees([]);
      setGuests([]);
      setPhotos([]);
    }
    setRemovedImageIds([]);
    setGuestInput("");
    setSearchMember("");
    setSelectedFamilyColor([]);
    setAppliedFamilyColor([]);
    setSelectedStates([]);
    setAppliedStates([]);
    setStateSearch("");
    setSortBy("name");
    setSortOrder("asc");
    setErrors({});
  }, [isOpen, editMode, newsDetails]);

  // Host choices: adult family members only. In-laws stay in the attendance table.
  const adultMembers = useMemo(
    () =>
      members
        .filter((member) => isAdultMember(member) && !isInLawMember(member))
        .sort(compareByName),
    [members]
  );

  /* Both attendance lists show alphabetically. Guests keep their original index
     so the remove button still targets the right entry after sorting. */
  const sortedGuests = useMemo(
    () =>
      guests
        .map((name, index) => ({ name, index }))
        .sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase(), undefined, {
            sensitivity: "base",
            numeric: true,
          })
        ),
    [guests]
  );

  const sortedAttendees = useMemo(
    () =>
      [...selectedAttendees].sort((a, b) =>
        String(a?.name || "")
          .toLowerCase()
          .localeCompare(String(b?.name || "").toLowerCase(), undefined, {
            sensitivity: "base",
            numeric: true,
          })
      ),
    [selectedAttendees]
  );

  /* Past two attendees the list names each line color once, as a heading with
     its members beneath it — the same shape the Events modal uses for invited
     family members. Every line gets its heading, however few it brought. */
  const attendeeGroups = useMemo(() => {
    const groups = [];
    sortedAttendees.forEach((member) => {
      const line = member?.color_name ? `${member.color_name} Line` : "No Line";
      let group = groups.find((entry) => entry.line === line);
      if (!group) {
        group = { line, hex: member?.hex_code || "#000000", members: [] };
        groups.push(group);
      }
      group.members.push(member);
    });
    return groups.sort((a, b) =>
      a.line.localeCompare(b.line, undefined, {
        sensitivity: "base",
        numeric: true,
      })
    );
  }, [sortedAttendees]);

  const filteredMembers = useMemo(() => {
    const term = searchMember.toLowerCase().trim();
    // Only people with an email on file (members and in-laws) are listed.
    let list = members.filter((member) =>
      Boolean(member?.email || member?.user?.email)
    );

    if (term) {
      list = list.filter((member) => {
        const haystack = [
          formatPersonName(member),
          getHeadOfLineName(member),
          member?.color_name,
          getMemberState(member),
          getMemberStateAbbr(member),
          member?.email || member?.user?.email,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      });
    }

    if (appliedFamilyColor.length > 0) {
      list = list.filter((member) => appliedFamilyColor.includes(member?.hex_code));
    }

    if (appliedStates.length > 0) {
      list = list.filter((member) => appliedStates.includes(getMemberState(member)));
    }

    const comparator =
      sortBy === "familyLine"
        ? compareByFamilyLine
        : sortBy === "state"
        ? compareByState
        : compareByName;
    list.sort((a, b) => (sortOrder === "asc" ? comparator(a, b) : -comparator(a, b)));
    return list;
  }, [members, searchMember, appliedFamilyColor, appliedStates, sortBy, sortOrder]);

  /* The 50 states, plus anything else the records actually hold, so a state
     written some other way is still selectable instead of silently missing. */
  const stateOptions = useMemo(() => {
    const known = new Set(stateFilterOptions.map((option) => option.value));
    const extras = [];
    members.forEach((member) => {
      const value = getMemberState(member);
      if (!value || known.has(value)) return;
      known.add(value);
      extras.push({ value, abbr: getStateAbbreviation(value), label: value });
    });
    if (extras.length === 0) return stateFilterOptions;
    return [...stateFilterOptions, ...extras].sort((a, b) =>
      a.value.localeCompare(b.value)
    );
  }, [members]);

  // What each filter trigger reads while its filter is applied
  const familyColorFilterValue = useMemo(() => {
    if (appliedFamilyColor.length === 0) return "";
    const names = appliedFamilyColor.map((hex) => {
      const entry = familyColorList.find((c) => c?.hex_code === hex);
      return entry?.color_name || entry?.name || "";
    });
    return (
      formatFilterValueList(names) || `${appliedFamilyColor.length} selected`
    );
  }, [appliedFamilyColor, familyColorList]);
  const stateFilterValue = useMemo(
    () =>
      formatFilterValueList(
        appliedStates.map((name) => getStateAbbreviation(name) || name),
        3
      ),
    [appliedStates]
  );

  const visibleStateOptions = useMemo(() => {
    const term = stateSearch.trim().toLowerCase();
    if (!term) return stateOptions;
    return stateOptions.filter((option) =>
      `${option.label} ${option.abbr || ""}`.toLowerCase().includes(term)
    );
  }, [stateOptions, stateSearch]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const isSelected = (list, member) => {
    const key = getMemberKey(member);
    return list.some((item) => item.id === key);
  };

  const toggleAttendee = (member) => {
    if (isView) return;
    const summary = toPersonSummary(member);
    setSelectedAttendees((prev) =>
      prev.some((item) => item.id === summary.id)
        ? prev.filter((item) => item.id !== summary.id)
        : [...prev, summary]
    );
  };

  const allFilteredSelected =
    filteredMembers.length > 0 &&
    filteredMembers.every((member) => isSelected(selectedAttendees, member));

  const toggleSelectAllFiltered = () => {
    if (isView) return;
    if (allFilteredSelected) {
      const keys = filteredMembers.map(getMemberKey);
      setSelectedAttendees((prev) => prev.filter((item) => !keys.includes(item.id)));
      return;
    }
    setSelectedAttendees((prev) => {
      const next = [...prev];
      filteredMembers.forEach((member) => {
        const summary = toPersonSummary(member);
        if (!next.some((item) => item.id === summary.id)) next.push(summary);
      });
      return next;
    });
  };

  const addGuest = () => {
    const name = guestInput.trim();
    if (!name) return;
    if (guests.some((guest) => guest.toLowerCase() === name.toLowerCase())) {
      setGuestInput("");
      return;
    }
    setGuests((prev) => [...prev, name]);
    setGuestInput("");
  };

  const handleGuestKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addGuest();
    }
  };

  const handlePhotoFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    try {
      const read = await Promise.all(files.map(readPhotoFile));
      setPhotos((prev) => [
        ...prev,
        ...read.map((photo, index) => ({
          id: `${Date.now()}_${index}_${Math.random().toString(36).slice(2, 7)}`,
          ...photo,
        })),
      ]);
    } catch (error) {
      toast.error(error?.message || "Could not read the selected photos");
    }
  }, []);

  /** Queues the ids of photos the server already holds, ignoring the ones that
      were only ever picked in this modal. */
  const rememberRemovedPhotos = (removed) => {
    const ids = removed
      .filter((photo) => !isNewPhoto(photo) && photo?.id)
      .map((photo) => photo.id);
    if (ids.length > 0) {
      setRemovedImageIds((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const removePhoto = (id) => {
    rememberRemovedPhotos(photos.filter((photo) => photo.id === id));
    setPhotos((prev) => prev.filter((photo) => photo.id !== id));
  };

  const removeAllPhotos = () => {
    rememberRemovedPhotos(photos);
    setPhotos([]);
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.date) {
      nextErrors.date = usesDateRange
        ? "Date range of event is required."
        : "Date of event is required.";
    } else if (usesDateRange && !form.endDate) {
      nextErrors.date = "Please pick both a start and an end date.";
    }
    if (!form.titleId) nextErrors.titleId = "Type of event is required.";
    if (!form.theme.trim()) nextErrors.theme = "Theme of event is required.";
    if (!form.location.trim()) nextErrors.location = "Location of event is required.";
    if (!form.host) nextErrors.host = "Host is required.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  /** Mirrors the entry onto the multipart/form-data body both POST
      /family-news and PUT /family-news/{id} take. Only the newly picked photos
      are uploaded — the ones already on the record stay put unless they were
      removed, which is a separate call. */
  const buildFormData = (entry) => {
    const formData = new FormData();
    formData.append("family_tree_id", familyTreeId || "");
    formData.append("host_id", form.host || "");
    formData.append("start_date", entry.date || "");
    formData.append("end_date", entry.end_date || "");
    formData.append("event_type_id", entry.title_id || "");
    formData.append("title", entry.title || "");
    formData.append("theme", entry.theme || "");
    formData.append("location", entry.location || "");
    // The record has no field of its own for the activities, so they are sent
    // as the description — which is where GET reads them back from.
    formData.append("participated_activities", entry.activities || "");
    formData.append("description", entry.activities || "");
    // Same story for the honoree's/host's words: no column of their own yet.
    formData.append("words_from_honoree", entry.words || "");

    // attendee_ids carries user ids only (same as the Events invite table).
    // Members with no account have no user_id, so they are not sent.
    entry.attendees.forEach((person) => {
      if (person?.user_id) formData.append("attendee_ids[]", person.user_id);
    });
    entry.guests.forEach((guest) => {
      formData.append("guests[]", guest);
    });
    entry.photos.filter(isNewPhoto).forEach((photo, index) => {
      const file = dataUrlToFile(photo.dataUrl, photo.name || `photo-${index}`);
      if (file) formData.append("photos[]", file);
    });

    return formData;
  };

  /**
   * Photos taken off the record are deleted for good, one call each, before
   * the update goes out. A photo that is already gone (404) counts as done;
   * anything else is reported and the update still runs, so the rest of the
   * edit is not lost.
   */
  const deleteRemovedImages = async (newsId) => {
    const failed = [];
    for (const imageId of removedImageIds) {
      try {
        const response = await deleteFamilyNewsImage(newsId, imageId);
        const status = response?.statusCode;
        if (status !== undefined && status !== 200 && status !== 404) {
          failed.push(imageId);
        }
      } catch (error) {
        console.error("Error deleting family news image:", error);
        failed.push(imageId);
      }
    }
    return failed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isView) return;
    if (!validate()) {
      toast.error("Please complete the required fields.");
      return;
    }

    setSaving(true);
    const isEdit = Boolean(newsDetails?.id);
    const entry = {
      date: form.date,
      end_date: usesDateRange ? form.endDate : "",
      title_id: form.titleId,
      title: form.title,
      theme: form.theme.trim(),
      location: form.location.trim(),
      activities: form.activities.trim(),
      words: form.words.trim(),
      attendees: selectedAttendees,
      guests,
      photos,
    };

    try {
      // Removing a photo is its own endpoint, so on an edit those calls run
      // first and the update follows.
      if (isEdit) {
        const failed = await deleteRemovedImages(newsDetails.id);
        if (failed.length > 0) {
          toast.error(
            `${failed.length} photo${failed.length === 1 ? "" : "s"} could not be removed`
          );
        }
      }

      // The axios interceptor resolves failed requests with the error
      // response (or undefined), so only an explicit 200/201 counts as saved.
      const response = isEdit
        ? await putFamilyNews(newsDetails.id, buildFormData(entry))
        : await postFamilyNews(buildFormData(entry));
      if (response?.statusCode !== 200 && response?.statusCode !== 201) {
        toast.error(response?.message || "Failed to save family news");
        return;
      }

      // The list re-fetches from GET /family-news on close, so nothing is
      // cached locally any more.
      toast.success(
        response?.message ||
          (editMode === "Edit" ? "Family news updated" : "Family news added")
      );
      onClose(true);
    } catch (error) {
      console.error("Error saving family news:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to save family news"
      );
    } finally {
      setSaving(false);
    }
  };

  const eventTypeOptions = useMemo(() => {
    const sorted = [...eventTypes].sort((a, b) => {
      const nameA = String(a?.name || "").toLowerCase();
      const nameB = String(b?.name || "").toLowerCase();
      const rank = (n) => (n === "misc" ? 2 : n === "other" ? 1 : 0);
      const diff = rank(nameA) - rank(nameB);
      if (diff !== 0) return diff;
      return nameA.localeCompare(nameB);
    });
    /* Labels read exactly as the Events modal's "Event Type" list does, so the
       two dropdowns stay in step: the types that open a range picker there are
       tagged "(date range)", and Misc is tagged as the one that never is.
       `name` keeps the plain type, which is what the card shows. */
    return sorted.map((eventType) => {
      const lowerName = String(eventType.name || "").toLowerCase();
      const name = lowerName === "family reunion" ? "Reunion" : eventType.name;

      let label = name;
      if (lowerName === "misc") {
        label = "Misc (no date range)";
      } else if (
        lowerName === "reunion" ||
        lowerName === "family reunion" ||
        lowerName === "family retreat" ||
        lowerName === "other"
      ) {
        label = `${name} (date range)`;
      }

      return { value: eventType.id, label, name };
    });
  }, [eventTypes]);

  /** Ids of the types tagged "(date range)" in the dropdown above. */
  const rangeTypeIds = useMemo(
    () =>
      new Set(
        eventTypes
          .filter((eventType) =>
            RANGE_EVENT_TYPES.has(String(eventType?.name || "").toLowerCase())
          )
          .map((eventType) => String(eventType.id))
      ),
    [eventTypes]
  );

  const usesDateRange = rangeTypeIds.has(String(form.titleId));

  /* The real range inputs are transparent (see .event-range-picker in App.css),
     so the picked range is painted over them as one string. */
  const rangeDisplay =
    form.date && form.endDate
      ? `${dayjs(form.date, "YYYY-MM-DD").format("M/D/YYYY")} - ${dayjs(
          form.endDate,
          "YYYY-MM-DD"
        ).format("M/D/YYYY")}`
      : "";

  /* Same option rendering as the Events modal's Host dropdown: the name column
     is sized to the longest name so every head-of-line + dot + colour group
     starts at the same x. */
  const hostOptions = useMemo(() => {
    const maxNameLen = adultMembers.reduce(
      (longest, member) => Math.max(longest, formatPersonName(member).length),
      0
    );
    const nameColWidth = `${maxNameLen}ch`;

    return adultMembers.map((member) => {
      const personName = formatPersonName(member);
      const headOfLineName = getHeadOfLineName(member) || "Start/Top of Line";
      const hexCode = member?.hex_code || "#000000";
      const colorName = member?.color_name ? `${member.color_name} Line` : "";

      return {
        value: getMemberKey(member),
        searchLabel: [personName, headOfLineName, colorName]
          .filter(Boolean)
          .join(" "),
        label: (
          <span
            style={{ display: "flex", alignItems: "center", gap: "1.25em", width: "100%" }}
          >
            <span style={{ width: nameColWidth, flexShrink: 0, whiteSpace: "nowrap" }}>
              {personName}
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                whiteSpace: "nowrap",
              }}
            >
              <span>{headOfLineName}</span>
              {colorName && (
                <>
                  <span
                    style={{
                      display: "inline-block",
                      width: "7px",
                      height: "7px",
                      flexShrink: 0,
                      borderRadius: "50%",
                      backgroundColor: hexCode,
                      verticalAlign: "middle",
                    }}
                  />
                  <span style={{ color: hexCode }}>{colorName}</span>
                </>
              )}
            </span>
          </span>
        ),
      };
    });
  }, [adultMembers]);

  /* A guest name with its remove control; view mode shows the name alone. */
  const renderGuestName = ({ name, index }) => (
    <>
      <span>{name}</span>
      {!isView && (
        <button
          type="button"
          aria-label={`Remove ${name}`}
          onClick={() => setGuests((prev) => prev.filter((_, i) => i !== index))}
        >
          ×
        </button>
      )}
    </>
  );

  /* ===== GUESTS IN ATTENDANCE =====
     Like the FM list below, this renders twice: beside FMs on desktop, and
     again on phones just above the FM list that sits over the members table.
     CSS shows whichever copy belongs to the current width. Phones list the
     guest names inside the FM box instead, so their copy is heading and
     input only. */
  const renderGuestsBlock = ({ showList }) => (
    <>
      <h6 className="heading family-news-list-heading">
        Guests in Attendance ({guests.length})
      </h6>
      {!isView && (
        <div className="family-news-guest-input">
          <input
            type="text"
            value={guestInput}
            placeholder="Type a guest name"
            onChange={(e) => setGuestInput(e.target.value)}
            onKeyDown={handleGuestKeyDown}
          />
          <button type="button" className="btn btn-apply" onClick={addGuest}>
            Add
          </button>
        </div>
      )}
      {showList && (
        <ul className="family-news-attendance-list">
          {sortedGuests.length === 0 ? (
            <li className="family-news-list-empty">No guests added yet</li>
          ) : (
            sortedGuests.map((guest) => (
              <li key={`${guest.name}-${guest.index}`}>{renderGuestName(guest)}</li>
            ))
          )}
        </ul>
      )}
    </>
  );

  /* ===== FMs IN ATTENDANCE =====
     Rendered in two places with identical markup: beside Guests on desktop,
     and again just above the members table on phones. CSS shows whichever
     one belongs to the current width and hides the other. Guests keep their
     own box beside this one at both widths. */
  const renderFmsBlock = () => (
    <>
      <h6 className="heading family-news-list-heading">
        FMs in Attendance ({selectedAttendees.length})
      </h6>
      <ul className="family-news-attendance-list">
        {sortedAttendees.length === 0 ? (
          <li className="family-news-list-empty">No members selected</li>
        ) : (
          attendeeGroups.map((group) => (
            <li
              className="family-news-attendance-group"
              key={group.line}
            >
              {/* The line reads once, carrying the only per-line count, and
                  its members list under it in black. */}
              <span
                className="family-news-list-line"
                style={{ color: group.hex }}
              >
                {group.members.length > 1
                  ? `${group.line} (${group.members.length})`
                  : group.line}
              </span>
              {group.members.map((member) => (
                <span
                  className="family-news-attendance-group-name"
                  key={member.id}
                >
                  {member.name}
                </span>
              ))}
            </li>
          ))
        )}
      </ul>
    </>
  );

  return (
    <>
      <button
        type="button"
        className="event-modal-close"
        onClick={() => onClose(false)}
        aria-label="Close"
      >
        ×
      </button>

      <div className="family-news-modal-header">
        <h2 className="event-modal-title">
          {editMode === "Add"
            ? "Add Family Gathering"
            : editMode === "Edit"
            ? "Edit Family Gathering"
            : "View Family Gathering"}
        </h2>
        <p className="event-modal-desc">
          {editMode === "View"
            ? "Viewing family gathering details"
            : "Share what happened — the details below appear on the Family Gathering card."}
        </p>
      </div>

      <form className="event-form" onSubmit={handleSubmit}>
        <div className="event-form-grid">
          {/* ===== PHOTO SUBMISSIONS ===== */}
          <div className="event-left-flex">
            <h6 className="heading">Photo Submissions</h6>

            <input
              ref={multiPhotoInputRef}
              type="file"
              accept={ACCEPTED_PHOTOS}
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                handlePhotoFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <input
              ref={singlePhotoInputRef}
              type="file"
              accept={ACCEPTED_PHOTOS}
              style={{ display: "none" }}
              onChange={(e) => {
                handlePhotoFiles(e.target.files);
                e.target.value = "";
              }}
            />

            <div
              className="event-banner-placeholder family-news-photo-drop"
              onClick={() => !isView && multiPhotoInputRef.current?.click()}
              style={{ cursor: isView ? "default" : "pointer" }}
            >
              {photos.length === 0 ? (
                <span className="event-banner-plus">+</span>
              ) : (
                <div className="family-news-photo-grid">
                  {photos.map((photo) => (
                    <div className="family-news-photo-thumb" key={photo.id}>
                      <img src={photo.dataUrl} alt={photo.name || "Photo"} />
                      {!isView && (
                        <button
                          type="button"
                          className="family-news-photo-remove"
                          aria-label={`Remove ${photo.name || "photo"}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            removePhoto(photo.id);
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!isView && (
              <div className="family-news-photo-actions">
                {/* One picker for all / several photos, one for a single photo */}
                <button
                  type="button"
                  className="btn btn-apply"
                  onClick={() => multiPhotoInputRef.current?.click()}
                >
                  Select Photos
                </button>
                <button
                  type="button"
                  className="btn btn-clear"
                  onClick={() => singlePhotoInputRef.current?.click()}
                >
                  Add One Photo
                </button>
                {photos.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-clear"
                    onClick={removeAllPhotos}
                  >
                    Clear All
                  </button>
                )}
              </div>
            )}
            <p className="family-news-photo-count">
              {photos.length} photo{photos.length === 1 ? "" : "s"} attached
              {!isView && " — select all, several, or just one."}
            </p>

            {/* Guests, FMs and the total scroll together as one block on
                phones rather than each list scrolling on its own. */}
            <div className="family-news-attendance-section">
              {/* Two columns side by side on desktop: FMs left justified,
                  Guests starting 5 spaces past the longest FM line. Phones
                  keep them stacked — two columns need ~380px. */}
              <div className="family-news-attendance-columns">
                <div className="family-news-attendance-column family-news-attendance-column--guests">
                  {renderGuestsBlock({ showList: true })}
                </div>

                <div className="family-news-attendance-column family-news-attendance-column--fms">
                  {renderFmsBlock()}
                </div>
              </div>

              <p className="heading family-news-attendance-total">
                Total in attendance: {guests.length + selectedAttendees.length}
              </p>
            </div>
          </div>

          {/* ===== DETAILS ===== */}
          <div className="event-form-fields event-form-fields--compact family-news-fields">
            <div className="event-field-row">
              {/* Host: family members 18 and older only */}
              <div className="event-field">
                <label className="required-label">Host</label>
                <DropdownSelect
                  options={hostOptions}
                  value={form.host}
                  onChange={(value) => setForm((prev) => ({ ...prev, host: value }))}
                  placeholder="Select Host"
                  disabled={isView}
                  className="event-dropdown"
                />
                {errors.host && <span className="family-news-error">{errors.host}</span>}
              </div>

              {/* Phones stack the row, so Date and Type share this pair to
                  stay side by side there; desktop sees straight through it. */}
              <div className="family-news-date-type-pair">
              <div className="event-field event-date-field">
                <label className="required-label">Date of Event</label>
                {/* Range types get the same two-ended picker the Events modal
                    shows for them; every other type keeps a single date. */}
                {usesDateRange ? (
                  <div className="event-range-picker-wrapper">
                    <RangePicker
                      className="event-range-picker"
                      value={
                        form.date && form.endDate
                          ? [
                              dayjs(form.date, "YYYY-MM-DD"),
                              dayjs(form.endDate, "YYYY-MM-DD"),
                            ]
                          : null
                      }
                      onChange={(dates) =>
                        setForm((prev) => ({
                          ...prev,
                          date: dates?.[0]?.isValid()
                            ? dates[0].format("YYYY-MM-DD")
                            : "",
                          endDate: dates?.[1]?.isValid()
                            ? dates[1].format("YYYY-MM-DD")
                            : "",
                        }))
                      }
                      format="M/D/YYYY"
                      separator="-"
                      allowClear
                      placeholder={["Start date", "End date"]}
                      disabled={isView}
                      getPopupContainer={(triggerNode) => triggerNode.parentNode}
                      style={{
                        width: "100%",
                        border: "1px solid #D9D9D9",
                        borderRadius: "8px",
                        padding: "6px 12px",
                        height: "36px",
                        fontSize: "12px",
                      }}
                    />
                    <div className="event-range-picker-display" aria-hidden="true">
                      {rangeDisplay || "Start date - End date"}
                    </div>
                  </div>
                ) : (
                  <DatePicker
                    value={form.date ? dayjs(form.date, "YYYY-MM-DD") : null}
                    onChange={(date) =>
                      setForm((prev) => ({
                        ...prev,
                        date: date && date.isValid() ? date.format("YYYY-MM-DD") : "",
                      }))
                    }
                    format="MM/DD/YYYY"
                    placeholder="Select date"
                    disabled={isView}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    style={{
                      width: "100%",
                      border: "1px solid #D9D9D9",
                      borderRadius: "8px",
                      padding: "6px 12px",
                      height: "36px",
                      fontSize: "12px",
                    }}
                  />
                )}
                {errors.date && <span className="family-news-error">{errors.date}</span>}
              </div>

              <div className="event-field">
                <label className="required-label">Type of Event</label>
                <DropdownSelect
                  options={eventTypeOptions}
                  value={form.titleId}
                  onChange={(value) => {
                    const picked = eventTypeOptions.find(
                      (option) => String(option.value) === String(value)
                    );
                    setForm((prev) => ({
                      ...prev,
                      titleId: value,
                      // The card shows the plain type, not the "(date range)"
                      // hint the dropdown adds.
                      title: picked?.name || "",
                      // A single-date type has no end date to carry over.
                      endDate: rangeTypeIds.has(String(value)) ? prev.endDate : "",
                    }));
                  }}
                  placeholder="Select Type"
                  disabled={isView}
                  className="event-dropdown"
                />
                {errors.titleId && (
                  <span className="family-news-error">{errors.titleId}</span>
                )}
              </div>
              </div>
            </div>

            <div className="event-field-row">
              <div className="event-field" style={{ flex: "0 1 32%" }}>
                <label className="required-label">Theme of Event</label>
                <input
                  type="text"
                  value={form.theme}
                  disabled={isView}
                  placeholder="e.g. Roaring Twenties"
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, theme: e.target.value }))
                  }
                  style={{ height: "36px" }}
                />
                {errors.theme && <span className="family-news-error">{errors.theme}</span>}
              </div>

              <div className="event-field" style={{ flex: "1 1 0" }}>
                <label className="required-label">Location of Event</label>
                <input
                  type="text"
                  value={form.location}
                  disabled={isView}
                  placeholder="e.g. Riverfront Park, Atlanta GA"
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, location: e.target.value }))
                  }
                  style={{ height: "36px" }}
                />
                {errors.location && (
                  <span className="family-news-error">{errors.location}</span>
                )}
              </div>
            </div>

            <div className="event-field-row">
              {/* Free text; the commas are what split the list onto separate
                  lines when the card renders it. */}
              <div className="event-field" style={{ flex: "1 1 100%" }}>
                <label>Participated Activities</label>
                <input
                  type="text"
                  value={form.activities}
                  disabled={isView}
                  placeholder="e.g. Day Cruise, Dinner, Movie"
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, activities: e.target.value }))
                  }
                  style={{ height: "36px" }}
                />
              </div>
            </div>

            <div className="event-field-row">
              {/* Free text over several lines — the card shows it in a fixed
                  two-inch box that scrolls, however long it runs. */}
              <div className="event-field" style={{ flex: "1 1 100%" }}>
                <label>Words from the Honoree and/or Host</label>
                <textarea
                  className="family-news-words-input"
                  value={form.words}
                  disabled={isView}
                  rows={4}
                  placeholder="Share a note from the honoree and/or host"
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, words: e.target.value }))
                  }
                />
              </div>
            </div>

            {/* ===== FAMILY IN ATTENDANCE ===== */}
            <div className="popup-header invite-header family-news-section-header">
              <h2>Family in Attendance ({selectedAttendees.length})</h2>
            </div>

            <div className="invite-controls event-invite-controls">
              <div className="invite-search event-search">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search..."
                  value={searchMember}
                  onChange={(e) => setSearchMember(e.target.value)}
                />
              </div>
              <div className="event-invite-filters">
                <div ref={colorRef} className="selctions-option position-relative">
                  <div
                    className={filterTriggerClass(
                      "selection-drop family-color-drop",
                      Boolean(familyColorFilterValue)
                    )}
                    style={{ fontWeight: "bold" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowFamilyColor((prev) => !prev);
                    }}
                  >
                    <FilterTriggerLabel label="Line/Color" value={familyColorFilterValue} />
                  </div>
                  {showFamilyColor && (
                    <div
                      className="member-list"
                      style={{ width: "300px", minWidth: "200px" }}
                    >
                      {familyColorList.length === 0 ? (
                        <p>There is no family color</p>
                      ) : (
                        familyColorList.map((color, index) => (
                          <div className="member-item" key={color?.hex_code || index}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedFamilyColor.includes(color.hex_code)}
                                onChange={(e) =>
                                  setSelectedFamilyColor((prev) =>
                                    e.target.checked
                                      ? [...prev, color.hex_code]
                                      : prev.filter((hex) => hex !== color.hex_code)
                                  )
                                }
                              />
                              <div style={{ flex: 1 }}>
                                {renderNameWithColor(
                                  formatDashboardFamilyLineHeadName(color) ||
                                    "Start/Top of Line",
                                  color?.hex_code || "#000000",
                                  color?.color_name
                                    ? `${color.color_name} Line`
                                    : color?.name
                                    ? `${color.name} Line`
                                    : ""
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                      <div
                        className="btn-container"
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginTop: "10px",
                          paddingTop: "10px",
                          borderTop: "1px solid #e0e0e0",
                        }}
                      >
                        <button
                          type="button"
                          className="btn btn-clear"
                          style={{ flex: 1 }}
                          onClick={() => {
                            setSelectedFamilyColor([]);
                            setAppliedFamilyColor([]);
                            setShowFamilyColor(false);
                          }}
                        >
                          Clear
                        </button>
                        <button
                          type="button"
                          className="btn btn-apply"
                          style={{ flex: 1 }}
                          onClick={() => {
                            setAppliedFamilyColor(selectedFamilyColor);
                            setShowFamilyColor(false);
                          }}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div ref={stateRef} className="selctions-option position-relative">
                  <div
                    className={filterTriggerClass(
                      "selection-drop family-color-drop",
                      Boolean(stateFilterValue)
                    )}
                    style={{ fontWeight: "bold" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowStateFilter((prev) => !prev);
                    }}
                  >
                    <FilterTriggerLabel label="State" value={stateFilterValue} />
                  </div>
                  {showStateFilter && (
                    <div
                      className="member-list family-news-state-list"
                      style={{ width: "260px", minWidth: "200px" }}
                    >
                      <div className="family-news-state-search">
                        <input
                          type="text"
                          className="search-input"
                          placeholder="Search state..."
                          value={stateSearch}
                          onChange={(e) => setStateSearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {visibleStateOptions.length === 0 ? (
                        <p>No matching state</p>
                      ) : (
                        visibleStateOptions.map((option) => (
                          <div className="member-item" key={option.value}>
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                margin: 0,
                                cursor: "pointer",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedStates.includes(option.value)}
                                onChange={(e) =>
                                  setSelectedStates((prev) =>
                                    e.target.checked
                                      ? [...prev, option.value]
                                      : prev.filter((name) => name !== option.value)
                                  )
                                }
                              />
                              <span style={{ flex: 1 }}>{option.label}</span>
                            </label>
                          </div>
                        ))
                      )}
                      <div
                        className="btn-container"
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginTop: "10px",
                          paddingTop: "10px",
                          borderTop: "1px solid #e0e0e0",
                        }}
                      >
                        <button
                          type="button"
                          className="btn btn-clear"
                          style={{ flex: 1 }}
                          onClick={() => {
                            setSelectedStates([]);
                            setAppliedStates([]);
                            setStateSearch("");
                            setShowStateFilter(false);
                          }}
                        >
                          Clear
                        </button>
                        <button
                          type="button"
                          className="btn btn-apply"
                          style={{ flex: 1 }}
                          onClick={() => {
                            setAppliedStates(selectedStates);
                            setShowStateFilter(false);
                          }}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Phones repeat Guests and the FM list here, directly above the
               table the FM list mirrors; the desktop pair by the photos is
               hidden at this width. The two sit side by side as narrow
               columns, Guests left, so the fields above them keep the height
               the pair would otherwise take stacked. Each list carries its
               own names. */}
            <div className="family-news-attendance-mobile">
              <div className="family-news-guests-mobile">
                {renderGuestsBlock({ showList: true })}
              </div>
              <div className="family-news-fms-mobile">
                {renderFmsBlock()}
              </div>
            </div>

            <div className="customer-tablle-main event-invite-table">
              <div className="table-scroll-container">
                <table className="event-invite-members-table">
                  <colgroup>
                    <col style={{ width: 32 }} />
                    <col className="fn-col-name" />
                    <col className="fn-col-line" />
                    <col className="fn-col-state" />
                    {showEmailColumn && <col className="fn-col-email" />}
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={{ width: 32, padding: "4px 2px 4px 8px" }}>
                        <input
                          type="checkbox"
                          disabled={isView}
                          checked={allFilteredSelected}
                          onChange={toggleSelectAllFiltered}
                        />
                      </th>
                      <th
                        onClick={() => handleSort("name")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <span>Name</span>
                          <span
                            style={{
                              fontSize: "12px",
                              color: sortBy === "name" ? "#025FEB" : "#666",
                            }}
                          >
                            {sortBy === "name"
                              ? sortOrder === "asc"
                                ? "↑"
                                : "↓"
                              : "↕"}
                          </span>
                        </span>
                      </th>
                      <th
                        onClick={() => handleSort("familyLine")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <span>Family Line/Color</span>
                          <span
                            style={{
                              fontSize: "12px",
                              color: sortBy === "familyLine" ? "#025FEB" : "#666",
                            }}
                          >
                            {sortBy === "familyLine"
                              ? sortOrder === "asc"
                                ? "↑"
                                : "↓"
                              : "↕"}
                          </span>
                        </span>
                      </th>
                      <th
                        onClick={() => handleSort("state")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <span>State</span>
                          <span
                            style={{
                              fontSize: "12px",
                              color: sortBy === "state" ? "#025FEB" : "#666",
                            }}
                          >
                            {sortBy === "state"
                              ? sortOrder === "asc"
                                ? "↑"
                                : "↓"
                              : "↕"}
                          </span>
                        </span>
                      </th>
                      {showEmailColumn && <th>Email</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.length > 0 ? (
                      filteredMembers.map((member, index) => (
                        <tr key={getMemberKey(member) || index}>
                          <td style={{ width: 32, padding: "4px 2px 4px 8px" }}>
                            <label style={{ margin: 0, display: "inline-flex" }}>
                              <input
                                type="checkbox"
                                disabled={isView}
                                checked={isSelected(selectedAttendees, member)}
                                onChange={() => toggleAttendee(member)}
                              />
                            </label>
                          </td>
                          <td>
                            <div className="custom-table-profile">
                              <h6 className="mb-0">{formatPersonName(member)}</h6>
                            </div>
                          </td>
                          <td>
                            {renderNameWithColor(
                              getHeadOfLineName(member) || "Start/Top of Line",
                              member?.hex_code || "#000000",
                              member?.color_name ? `${member.color_name} Line` : ""
                            )}
                          </td>
                          <td title={getMemberState(member) || ""}>
                            {getMemberStateAbbr(member) || "-"}
                          </td>
                          {showEmailColumn && (
                            <td>{member?.email || member?.user?.email || "-"}</td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={showEmailColumn ? 5 : 4} className="text-center py-3 text-gray-500">
                          No members found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <p className="family-news-folder-hint family-news-folder-hint--footer">
          Auto-archives into "{DEFAULT_ARCHIVE_FOLDER}" 30 days after the event date.
        </p>

        <div className="event-form-actions">
          <button
            type="button"
            className="event-btn-cancel"
            onClick={() => onClose(false)}
          >
            {isView ? "Close" : "Cancel"}
          </button>
          {!isView && (
            <button type="submit" className="event-btn-save" disabled={saving}>
              {saving ? (
                <CircularProgress size={20} color="inherit" />
              ) : editMode === "Edit" ? (
                "Update News"
              ) : (
                "Save News"
              )}
            </button>
          )}
        </div>
      </form>
    </>
  );
}

export default CreateFamilyNewsModal;
